import { FC } from 'react';
import clsx from 'clsx';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import { pageWindow } from '../../data-table/pageWindow';
import './Pagination.scss';

export interface IPaginationProps {
    /** Текущая страница, с 1 */
    value: number;
    /** Число страниц */
    total: number;
    /** Выбор страницы (с 1) */
    onChange: (page: number) => void;
    /** Все кнопки недоступны (идёт загрузка, отказ) */
    disabled?: boolean;
    /** Имя кнопки «назад» @default 'Предыдущая страница' */
    previousLabel?: string;
    /** Имя кнопки «вперёд» @default 'Следующая страница' */
    nextLabel?: string;
    /**
     * Имя кнопки номера страницы; `null` — без своего имени (имя — номер)
     * @default (page) => `Страница ${page}`
     */
    pageLabel?: ((page: number) => string) | null;
    /**
     * Префикс классов футера таблицы (`dtb`, `atb`): корень — `<prefix>__pager`, многоточие —
     * `<prefix>__pager-gap`, вид — от таблицы. Без префикса — `rf-pagination` со своим видом.
     */
    classPrefix?: string;
    className?: string;
    /** Тест-якорь корня */
    'data-testid'?: string;
}

const defaultPageLabel = (page: number) => `Страница ${page}`;

/**
 * Пейджер: «назад», номера (первая, последняя, текущая и соседние, между ними — многоточие), «вперёд»; текущая —
 * `aria-current="page"`. Вид — как у пейджера DataTable.
 */
const Pagination: FC<IPaginationProps> = ({
    value,
    total,
    onChange,
    disabled = false,
    previousLabel = 'Предыдущая страница',
    nextLabel = 'Следующая страница',
    pageLabel = defaultPageLabel,
    classPrefix,
    className,
    'data-testid': testId,
}) => {
    const pageCount = Math.max(1, total);
    const current = Math.min(Math.max(1, value), pageCount);
    const root = classPrefix ? `${classPrefix}__pager` : 'rf-pagination';
    const gap = classPrefix ? `${classPrefix}__pager-gap` : 'rf-pagination__gap';

    return (
        <div className={clsx(root, className)} data-testid={testId}>
            <button
                type="button"
                aria-label={previousLabel}
                disabled={disabled || current <= 1}
                onClick={() => onChange(current - 1)}
            >
                <HiChevronLeft aria-hidden />
            </button>
            {pageWindow(current - 1, pageCount).map((page, i) =>
                page === 'gap' ? (
                    <span key={`gap-${i}`} className={gap}>
                        …
                    </span>
                ) : (
                    <button
                        key={page}
                        type="button"
                        className={page === current ? 'is-current' : undefined}
                        aria-current={page === current ? 'page' : undefined}
                        aria-label={pageLabel?.(page)}
                        disabled={disabled}
                        onClick={() => onChange(page)}
                    >
                        {page}
                    </button>
                ),
            )}
            <button
                type="button"
                aria-label={nextLabel}
                disabled={disabled || current >= pageCount}
                onClick={() => onChange(current + 1)}
            >
                <HiChevronRight aria-hidden />
            </button>
        </div>
    );
};

export default Pagination;
